import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

export type AccessStatus = 'waitlist' | 'active' | 'suspended'

interface AccessStatusResult {
  status: AccessStatus | null
  isLoading: boolean
  isWaitlisted: boolean
  isActive: boolean
  isSuspended: boolean
  error: Error | null
}

/**
 * Hook to query the current user's access status from the database
 *
 * Access statuses:
 * - 'waitlist': User is waiting for approval
 * - 'active': User has full access to the app
 * - 'suspended': User is blocked from accessing the app
 *
 * Uses direct Supabase query with RLS policies for performance.
 */
export function useAccessStatus(): AccessStatusResult {
  const { user } = useAuth()

  const { data, isLoading, error } = useQuery({
    queryKey: ['access-status', user?.id],
    queryFn: async () => {
      if (!user?.id) return null

      const { data, error } = await supabase
        .from('profiles')
        .select('access_status')
        .eq('id', user.id)
        .single()

      if (error) throw error
      return data?.access_status as AccessStatus
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  return {
    status: data ?? null,
    isLoading,
    isWaitlisted: data === 'waitlist',
    isActive: data === 'active',
    isSuspended: data === 'suspended',
    error: error as Error | null,
  }
}
